import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import React from "react";
var __jsx = React.createElement;
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useSession } from "@/_MiniApps/auth/AuthContext";
import useLocalStorage from "@hooks/useLocalStorage";
import { api__checkConsentRequired, api__acceptConsent, api__fetchPrivacyPolicyData, api__fetchTermsConditionsData } from "@/apis/consent/consentApi";
import useIsWebView from "@hooks/useIsWebview";
var CONSENT_STORAGE_KEY = "consent_data";
var LAST_CHECKED_KEY = "consent_last_checked";
var CONSENT_ACCEPTED_KEY = "consent_accepted";
var CONSENT_REQUIRED_KEY = "consent_required";
export var ConsentContext = /*#__PURE__*/createContext(undefined);
export var ConsentContextProvider = function ConsentContextProvider(_ref) {
  var children = _ref.children;
  var _useSession = useSession(),
    isLoggedIn = _useSession.isLoggedIn,
    user = _useSession.user,
    setUserData = _useSession.setUserData;
  var _useLocalStorage = useLocalStorage(),
    getLocalStorage = _useLocalStorage.getLocalStorage,
    setLocalStorage = _useLocalStorage.setLocalStorage;
  var hasCheckedRef = useRef(false);
  var isWebView = useIsWebView();
  var _useState = useState(function () {
      var stored = getLocalStorage(CONSENT_STORAGE_KEY);
      var lastChecked = getLocalStorage(LAST_CHECKED_KEY);
      var consentRequired = getLocalStorage(CONSENT_REQUIRED_KEY);
      var consentAccepted = getLocalStorage(CONSENT_ACCEPTED_KEY);

      // If consent is required but not accepted, show modal immediately
      var shouldShowModal = consentRequired === 'true' && consentAccepted !== 'true' && stored;
      return {
        isLoading: false,
        consentInfo: stored ? JSON.parse(stored) : null,
        showConsentModal: shouldShowModal,
        lastCheckedDate: lastChecked
      };
    }),
    consentState = _useState[0],
    setConsentState = _useState[1];

  // Helper function to create consent info from API response
  var helper__createConsentInfo = useCallback(function (data) {
    var _data$consents_versio, _data$consents_versio2;
    return {
      privacy_version: (data === null || data === void 0 ? void 0 : (_data$consents_versio = data.consents_version) === null || _data$consents_versio === void 0 ? void 0 : _data$consents_versio.privacy_version) || '',
      terms_version: (data === null || data === void 0 ? void 0 : (_data$consents_versio2 = data.consents_version) === null || _data$consents_versio2 === void 0 ? void 0 : _data$consents_versio2.terms_version) || '',
      privacy_policy_url: (data === null || data === void 0 ? void 0 : data.privacy_policy_url) || '',
      terms_conditions_url: (data === null || data === void 0 ? void 0 : data.terms_conditions_url) || '',
      last_updated: (data === null || data === void 0 ? void 0 : data.last_updated) || '',
      current_privacy_version: data === null || data === void 0 ? void 0 : data.current_privacy_version,
      current_terms_version: data === null || data === void 0 ? void 0 : data.current_terms_version,
      title: data === null || data === void 0 ? void 0 : data.title,
      subtitle: data === null || data === void 0 ? void 0 : data.subtitle,
      content: data === null || data === void 0 ? void 0 : data.content
    };
  }, []);

  // Check if consent check is required (once daily for logged-in users)
  var action__isConsentCheckRequired = useCallback(function () {
    if (!isLoggedIn) return false;

    // If we already know consent is required and not accepted, don't need to check API again
    var consentRequired = getLocalStorage(CONSENT_REQUIRED_KEY);
    var consentAccepted = getLocalStorage(CONSENT_ACCEPTED_KEY);
    if (consentRequired === 'true' && consentAccepted !== 'true') {
      return false; // Already know consent is required, don't check API again
    }
    if (consentRequired === 'false' || consentAccepted === 'true') {
      // Check if we should re-check today (daily check)
      var lastChecked = getLocalStorage(LAST_CHECKED_KEY);
      if (!lastChecked) return true;
      var today = new Date().toDateString();
      var lastCheckedDate = new Date(lastChecked).toDateString();
      return today !== lastCheckedDate;
    }
    return true; // No stored state, need to check
  }, [isLoggedIn, getLocalStorage]);

  // Check if consent is required from API
  var action__checkConsentRequired = useCallback(/*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee() {
    var _user$consents_versio, _user$consents_versio2, currentTermsVersion, currentPolicyVersion, response, today, consentInfo, _t;
    return _regenerator().w(function (_context) {
      while (1) switch (_context.p = _context.n) {
        case 0:
          if (!isWebView) {
            _context.n = 1;
            break;
          }
          return _context.a(2, false);
        case 1:
          if (!(!isLoggedIn || hasCheckedRef.current)) {
            _context.n = 2;
            break;
          }
          return _context.a(2, false);
        case 2:
          _context.p = 2;
          hasCheckedRef.current = true;
          setConsentState(function (prev) {
            return _objectSpread(_objectSpread({}, prev), {}, {
              isLoading: true
            });
          });

          // Get user's current consent versions from local storage
          currentTermsVersion = (user === null || user === void 0 ? void 0 : (_user$consents_versio = user.consents_version) === null || _user$consents_versio === void 0 ? void 0 : _user$consents_versio.terms_version) || null;
          currentPolicyVersion = (user === null || user === void 0 ? void 0 : (_user$consents_versio2 = user.consents_version) === null || _user$consents_versio2 === void 0 ? void 0 : _user$consents_versio2.privacy_version) || null;
          _context.n = 3;
          return api__checkConsentRequired(currentTermsVersion, currentPolicyVersion);
        case 3:
          response = _context.v;
          today = new Date().toISOString(); // Update last checked date
          setLocalStorage(LAST_CHECKED_KEY, today);
          setConsentState(function (prev) {
            return _objectSpread(_objectSpread({}, prev), {}, {
              lastCheckedDate: today
            });
          });
          if (!(response !== null && response !== void 0 && response.requires_consent)) {
            _context.n = 4;
            break;
          }
          consentInfo = helper__createConsentInfo(response); // Store consent data and requirement status
          setLocalStorage(CONSENT_STORAGE_KEY, JSON.stringify(consentInfo));
          setLocalStorage(CONSENT_REQUIRED_KEY, 'true');
          setLocalStorage(CONSENT_ACCEPTED_KEY, 'false');
          setConsentState(function (prev) {
            return _objectSpread(_objectSpread({}, prev), {}, {
              consentInfo: consentInfo,
              showConsentModal: true
            });
          });
          return _context.a(2, true);
        case 4:
          // Clear any previous consent requirements
          setLocalStorage(CONSENT_REQUIRED_KEY, 'false');
          setLocalStorage(CONSENT_ACCEPTED_KEY, 'true');
        case 5:
          return _context.a(2, false);
        case 6:
          _context.p = 6;
          _t = _context.v;
          console.error("Error checking consent requirements:", _t);
          return _context.a(2, false);
        case 7:
          _context.p = 7;
          setConsentState(function (prev) {
            return _objectSpread(_objectSpread({}, prev), {}, {
              isLoading: false
            });
          });
          return _context.f(7);
        case 8:
          return _context.a(2);
      }
    }, _callee, null, [[2, 6, 7, 8]]);
  })), [isLoggedIn, setLocalStorage, helper__createConsentInfo, getLocalStorage, isWebView]);

  // Accept consent (Privacy Policy and Terms & Conditions)
  var action__acceptConsent = useCallback(/*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee2() {
    var response, _t2;
    return _regenerator().w(function (_context2) {
      while (1) switch (_context2.p = _context2.n) {
        case 0:
          _context2.p = 0;
          setConsentState(function (prev) {
            return _objectSpread(_objectSpread({}, prev), {}, {
              isLoading: true
            });
          });
          _context2.n = 1;
          return api__acceptConsent();
        case 1:
          response = _context2.v;
          if (!response) {
            _context2.n = 2;
            break;
          }
          // Mark consent as accepted persistently
          setLocalStorage(CONSENT_ACCEPTED_KEY, 'true');
          setLocalStorage(CONSENT_REQUIRED_KEY, 'false');
          setLocalStorage(CONSENT_STORAGE_KEY, "");
          user.consents_version = {
            privacy_version: response.consents_version.privacy_version,
            terms_version: response.consents_version.terms_version
          };
          setUserData(user);
          setConsentState(function (prev) {
            return _objectSpread(_objectSpread({}, prev), {}, {
              showConsentModal: false,
              consentInfo: null
            });
          });
          _context2.n = 3;
          break;
        case 2:
          throw new Error("Failed to accept consent");
        case 3:
          _context2.n = 5;
          break;
        case 4:
          _context2.p = 4;
          _t2 = _context2.v;
          console.error("Error accepting consent:", _t2);
          throw _t2;
        case 5:
          _context2.p = 5;
          setConsentState(function (prev) {
            return _objectSpread(_objectSpread({}, prev), {}, {
              isLoading: false
            });
          });
          return _context2.f(5);
        case 6:
          return _context2.a(2);
      }
    }, _callee2, null, [[0, 4, 5, 6]]);
  })), [setLocalStorage]);

  // Refresh consent check (force check regardless of daily limit)
  var action__refreshConsentCheck = useCallback(/*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee3() {
    return _regenerator().w(function (_context3) {
      while (1) switch (_context3.n) {
        case 0:
          hasCheckedRef.current = false; // Reset the check flag
          setConsentState(function (prev) {
            return _objectSpread(_objectSpread({}, prev), {}, {
              lastCheckedDate: null
            });
          });
          _context3.n = 1;
          return action__checkConsentRequired();
        case 1:
          return _context3.a(2);
      }
    }, _callee3);
  })), [action__checkConsentRequired]);

  // Auto-check consent on login - ONLY ONCE
  useEffect(function () {
    if (!isLoggedIn || hasCheckedRef.current) return;
    var shouldCheck = action__isConsentCheckRequired();
    if (shouldCheck) {
      action__checkConsentRequired().then();
    }
  }, [isLoggedIn]); // Only depend on isLoggedIn

  var fetch__privacyPolicyData = useCallback(/*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee4() {
    return _regenerator().w(function (_context4) {
      while (1) switch (_context4.n) {
        case 0:
          return _context4.a(2, api__fetchPrivacyPolicyData().then(function (data) {
            if (data) {
              return data;
            } else {
              throw new Error("Failed to fetch privacy policy");
            }
          }));
      }
    }, _callee4);
  })), []);
  var fetch__termsConditionsData = useCallback(/*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee5() {
    return _regenerator().w(function (_context5) {
      while (1) switch (_context5.n) {
        case 0:
          return _context5.a(2, api__fetchTermsConditionsData().then(function (data) {
            if (data) {
              return data;
            } else {
              throw new Error("Failed to fetch terms and conditions");
            }
          }));
      }
    }, _callee5);
  })), []);
  var actions = {
    action__acceptConsent: action__acceptConsent,
    action__refreshConsentCheck: action__refreshConsentCheck,
    action__checkConsentRequired: action__checkConsentRequired,
    action__isConsentCheckRequired: action__isConsentCheckRequired
  };
  var helpers = {
    helper__createConsentInfo: helper__createConsentInfo
  };
  var remote_actions = {
    fetch__privacyPolicyData: fetch__privacyPolicyData,
    fetch__termsConditionsData: fetch__termsConditionsData
  };
  var value = {
    actions: actions,
    consentState: consentState,
    helpers: helpers,
    remote_actions: remote_actions
  };
  return /*#__PURE__*/React.createElement(ConsentContext.Provider, {
    value: value
  }, children);
};
export var useConsent = function useConsent() {
  var context = useContext(ConsentContext);
  if (!context) {
    throw new Error('useConsent must be used within ConsentContextProvider');
  }
  return context;
};